import { supertypeClass, amorphicStatic } from '@haventech/amorphic';

import _ from 'lodash';
import { DocuSign, DocuSignEnvelope } from '../../../common/js/docusign/DocuSign';
import { Policy } from '../../../common/js/customer/Policy';
import { Person } from '../../../common/js/customer/Person';
import { CoverpathPolicy } from '../../../coverpath-common/classes/coverpath_subclasses/CoverpathPolicy';
import { CoverpathCustomer } from '../../../coverpath-common/classes/coverpath_subclasses/CoverpathCustomer';
import { CommonUtils } from '../../../common/js/static/CommonUtils';
import { LaunchDarklyInstance, LD_APP } from '../../../common/js/launch-darkly/LaunchDarklySingleton';
import { Utils } from '../../../common/js/Utils';
import { FormFieldText } from '../../../common/js/docusign/FormFieldText';
import { FormFieldCheckbox } from '../../../common/js/docusign/FormFieldCheckbox';
import { FormFieldRadioGroup } from '../../../common/js/docusign/FormFieldRadioGroup';
import { FormFieldSelect } from '../../../common/js/docusign/FormFieldSelect';
import { IllustrationRequestProperty } from '../../../common/js/customer/IllustrationRequestProperty';
import { FeatureFlags } from '../interfaces/CoverpathTypes';
import { IllustrationRequest } from '../../classes/wholelife/IllustrationRequest';
import { IllustrationService } from '../../classes/wholelife/IllustrationService';
import { RevisedBasicIllustrationUtils } from '../../classes/wholelife/RevisedBasicIllustrationUtils';
import { HtmlPdfController } from '../../classes/wholelife/basicIllustration/pdf/HtmlPdfController';
import { CoverpathUtils } from '../static/CoverpathUtils';
import { CasApplication } from '../../classes/coverpath_subclasses/CasApplication';
import { RiderType } from '../../../common/js/static/Product';
import { PolicyRider } from '../../../common/js/customer/PolicyRider';
import { Exchange1035 } from '../../../common/js/quote/coverpath/Exchange1035';
import { HtmlPdfUtils } from '../../../coverpath-common/classes/wholelife/HtmlPdfUtils';
import { SecurityUtils } from '../../js/static/SecurityUtils';
import { UICategory, UILoggerSingleton } from '../../../common/js/logger/UILoggerSingleton';

const ILLUSTRATION_PDF_DOCUMENT_ID = '8675310';
const MEC_PDF_DOCUMENT_ID = '8675311';
const INSURED_RECIPIENT_ID = '2';
const OWNER_RECIPIENT_ID = '3';
const SECOND_OWNER_RECIPIENT_ID = '4';
// this is because it's guaranteed to be the third document,
// the first being the app, second being the app signature
const SIGNATURE_PAGE_KEY = '2';
const ILLUSTRATION_FETCH_KEY = '3';
const MEC_FETCH_KEY = '4';

@supertypeClass({ toClient: ['admin'], toServer: true })
export class CoverpathDocuSign extends DocuSign {
  constructor() {
    super();
  }

  static new(controller?) {
    return new CoverpathDocuSign().init(controller);
  }

  async generateEnvelope(policy: Policy, formsAndComposites, isAdditionalPackage?: boolean): Promise<DocuSignEnvelope> {
    const forms = formsAndComposites.forms,
      composites = [],
      express = this.express,
      doSend = this.doSend,
      envMap = {
        docMap: {},
        recipientMap: {},
      },
      log = [];

    if (!forms || forms.length === 0) {
      throw new Error('Cannot find forms to send');
    }

    //list of form templates & their tabs
    const templates = [];
    let lastId = '';
    let dupCnt = 0;
    let numberOfPrefixedForms = DocuSign.includeApplicationAsPDF(policy) ? 1 : 0;
    if (CoverpathDocuSign.includeIllustrationAsPDF(policy)) {
      numberOfPrefixedForms++;
    }
    if (await CoverpathDocuSign.includeMecAsPDF(policy)) {
      numberOfPrefixedForms++;
    }

    numberOfPrefixedForms = isAdditionalPackage ? 0 : numberOfPrefixedForms;
    for (let i = 0, len = forms.length; i < len; ++i) {
      dupCnt = lastId === forms[i].form.templateId ? dupCnt + 1 : 0;
      lastId = forms[i].form.templateId;

      const sequence = String(i + 1 + numberOfPrefixedForms);
      const type = forms[i].form.getType();
      const formLanguage = forms[i].form.getLanguage() || 'en';

      const isSignature = /Signature/.test(forms[i].form.name);

      const mapFormDef = {
        sequence: isSignature ? SIGNATURE_PAGE_KEY : sequence,
        language: formLanguage,
      };

      if (type !== undefined) {
        if (envMap.docMap[type] === undefined) {
          envMap.docMap[type] = [mapFormDef];
        } else {
          envMap.docMap[type].push(mapFormDef);
        }
      }

      const template = {
        templateId: forms[i].form.templateId,
        seq: sequence,
        tabs: {
          listTabs: [],
          radioGroupTabs: [],
          checkBoxTabs: [],
          textTabs: [],
        },
      };

      const t = template.tabs;

      getFormTabs.call(
        this,
        t.listTabs,
        t.radioGroupTabs,
        t.checkBoxTabs,
        t.textTabs,
        forms[i].mappingForm || forms[i].form,
        dupCnt,
        log
      );

      templates.push(template);
    }

    // If owner != insured && insured != juvenile, need to send docusign to owner
    // If owner != insured && insured == juvenile && signer != owner, need to send docusign to owner
    // If Lifebridge policy, skip owner signature
    const ownerIsNotInsuredConsiderJuvy = function () {
      return (
        !policy.isLifeBridge() &&
        (policy.isOwnerOtherThanInsuredAndNotJuvenile() || policy.insuredIsJuvenileHasSigner())
      );
    };

    //list of recipients
    let rec;

    const signerList = [];
    const channelName = policy.getChannelInfo().name;
    const language = policy.submittedLanguage ? policy.submittedLanguage : 'en';
    const isInstant = policy.isAccelerated();
    const reqToCheck = _.find(this.customer.application.primaryPolicy.currentStageRequirements, function (req) {
      return (
        req === 'Send Insurability Statement for signature' || req === 'Resend Insurability Statement for signature'
      );
    });
    const emailContent = this.buildEmailContent(channelName, language, express);

    let name = policy.insured.person.getFullName();
    let accessCode = await SecurityUtils.getPiiValueByKey(policy.insured.person.SSN, 4);

    if (policy.insuredIsJuvenile()) {
      // If insured is juvenile, and owner is other than as signer, consider signer as insured for docusign
      if (policy.isOwnerOtherThanSigner()) {
        name = policy.insured.signer.getFullName();
        accessCode = await SecurityUtils.getPiiValueByKey(policy.insured.signer.SSN, 4);
      }
      // If insured is juvenile, and owner is same with signer, consider owner as insured for docusign
      else {
        name = policy.owner.getIsEntityOrTrust()
          ? policy.ownerEntity.authorizedSigner.getFullName()
          : policy.ownerPerson.getFullName();
        accessCode = policy.owner.getIsEntityOrTrust()
          ? policy.ownerEntity.getEINLast4()
          : await SecurityUtils.getPiiValueByKey(policy.ownerPerson.SSN, 4);
      }
    }

    let isDocuSignRedirectEnabled = false;
    let isAgentSubmittedAndNotAcceleratedApp = false;
    if (policy.isCoverpathPolicy()) {
      const ldInstance = await LaunchDarklyInstance.getInstance(LD_APP.cp);
      isDocuSignRedirectEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(
        FeatureFlags.isDocuSignRedirectEnabled,
        policy.primaryPolicyAgent.primaryAgent.producerId
      );
      isAgentSubmittedAndNotAcceleratedApp =
        (policy.application as CasApplication).isAgentSubmittedApp && !policy.isAccelerated();
    }

    rec = {
      name: name,
      // If insured is juvenile and has no signer (owner == signer), insured's email == owner's email, so this docusign is
      // actually sent to owner
      // If insured is juvenile and has signer (owner != signer), insured's email = signer's email, so this docusign is
      // actually sent to signer
      email: policy.insured.person.email,
      recipientId: INSURED_RECIPIENT_ID,
      routingOrder: '2',
      roleName: 'Insured',
      templateLocked: true,
      accessCode: accessCode,
      emailNotification: {
        supportedLanguage: language,
        emailBody: emailContent.body,
        emailSubject: emailContent.subject,
      },
    };

    if (isDocuSignRedirectEnabled && !ownerIsNotInsuredConsiderJuvy() && !isAgentSubmittedAndNotAcceleratedApp) {
      rec.clientUserId = CommonUtils.DOCUSIGN_REDIRECT_CLIENT_USER_ID;
    }

    signerList.push(rec);
    envMap.recipientMap['Insured'] = rec;

    // If owner != insured && insured != juvenile, need to send docusign to owner
    // If owner != insured && insured == juvenile && signer != owner, need to send docusign to owner
    // If Lifebridge policy, skip owner signature
    if (ownerIsNotInsuredConsiderJuvy()) {
      rec = {
        name: policy.getOwnerSigningName(),
        email: policy.owner.email,
        recipientId: OWNER_RECIPIENT_ID,
        routingOrder: '2',
        roleName: 'Owner',
        templateLocked: true,
        accessCode:
          policy.owner instanceof Person
            ? await SecurityUtils.getPiiValueByKey(policy.owner.SSN, 4)
            : policy.ownerEntity.getEINLast4(),
        emailNotification: {
          supportedLanguage: language,
          emailBody: emailContent.body,
          emailSubject: emailContent.subject,
        },
      };
      if (isDocuSignRedirectEnabled && !isAgentSubmittedAndNotAcceleratedApp) {
        rec.clientUserId = CommonUtils.DOCUSIGN_REDIRECT_CLIENT_USER_ID;
      }
      signerList.push(rec);
      envMap.recipientMap['Owner'] = rec;
    }

    if (policy.isJointOwnership()) {
      rec = {
        name: policy.otherOwner.getFullNameOfOwner(),
        email: policy.otherOwner.email,
        recipientId: SECOND_OWNER_RECIPIENT_ID,
        routingOrder: '2',
        roleName: 'SecondOwner',
        templateLocked: true,
        accessCode: await SecurityUtils.getPiiValueByKey(policy.otherOwner.SSN, 4),
        emailNotification: {
          supportedLanguage: language,
          emailBody: emailContent.body,
          emailSubject: emailContent.subject,
        },
      };

      signerList.push(rec);
      envMap.recipientMap['Owner'] = rec;
    }

    if (formsAndComposites.composites && formsAndComposites.composites.length > 0) {
      for (
        let index = 0, generatedCompositesLength = formsAndComposites.composites.length;
        index < generatedCompositesLength;
        index++
      ) {
        const composite = formsAndComposites.composites[index];

        if (this.compositeContainsDocOfId(composite, ILLUSTRATION_PDF_DOCUMENT_ID)) {
          const mapFormDef = {
            sequence: ILLUSTRATION_FETCH_KEY,
            language: 'en',
          };

          const type = 'BasicIllustration';
          if (envMap.docMap[type] === undefined) {
            envMap.docMap[type] = [mapFormDef];
          } else {
            envMap.docMap[type].push(mapFormDef);
          }
          UILoggerSingleton.getInstance().info(
            {
              module: 'CoverpathDocuSign',
              function: 'generateEnvelope',
              category: UICategory.request,
            },
            `Adding BasicIllustration to docMap for policy._id = ${policy._id}`
          );
        }

        if (this.compositeContainsDocOfId(composite, MEC_PDF_DOCUMENT_ID)) {
          const mapFormDef = {
            sequence: MEC_FETCH_KEY,
            language: 'en',
          };

          const type = 'a5030_mec';
          if (envMap.docMap[type] === undefined) {
            envMap.docMap[type] = [mapFormDef];
          } else {
            envMap.docMap[type].push(mapFormDef);
          }
          UILoggerSingleton.getInstance().info(
            {
              module: 'CoverpathDocuSign',
              function: 'generateEnvelope',
              category: UICategory.request,
            },
            `Adding MEC form to docMap for policy._id = ${policy._id}`
          );
        }

        const insuredTabs = {
          listTabs: [],
          radioGroupTabs: [],
          checkBoxTabs: [],
          textTabs: [],
          signHereTabs: [],
          dateSignedTabs: [],
        };

        let ownerTabs = null;
        // If owner IS NOT insured and insured IS juvenile, no need to ask owner (parent) to sign,
        // because above logic already allows owner (parent) to sign on behalf of insured (juvenile child)
        // If Lifebridge policy, skip owner signature
        if (!policy.isLifeBridge() && policy.isOwnerOtherThanInsuredAndNotJuvenile()) {
          ownerTabs = {
            listTabs: [],
            radioGroupTabs: [],
            checkBoxTabs: [],
            textTabs: [],
            signHereTabs: [],
            dateSignedTabs: [],
          };
        }
        let secondOwnerTabs = null;
        if (policy.isJointOwnership()) {
          secondOwnerTabs = {
            listTabs: [],
            radioGroupTabs: [],
            checkBoxTabs: [],
            textTabs: [],
            signHereTabs: [],
            dateSignedTabs: [],
          };
        }
        const illustrationSignPage = await this.getSignaturePageNumber(policy);
        this.setTabsForPdfComposite(composite, insuredTabs, ownerTabs, secondOwnerTabs, illustrationSignPage);
        this.assignTabsToComposite(composite, signerList, insuredTabs, ownerTabs, secondOwnerTabs);
        composites.push(composite);
      }
    }

    //make req to DocuSign to send envelope to recipients
    for (let iTemplate = 0, numTemplates = templates.length; iTemplate < numTemplates; ++iTemplate) {
      const envelopeTemplate = templates[iTemplate];

      let isSignature = false;
      // application is always first composite, and signature is always first template
      // signature must immediately follow application
      if (iTemplate === 0 && DocuSign.includeApplicationAsPDF(policy)) {
        isSignature = true;
      }

      const composite = {
        compositeTemplateId: new Date().getTime() + envelopeTemplate.seq,
        serverTemplates: [
          {
            sequence: envelopeTemplate.seq,
            templateId: envelopeTemplate.templateId,
          },
        ],
        inlineTemplates: [
          {
            sequence: isSignature ? SIGNATURE_PAGE_KEY : envelopeTemplate.seq,
            recipients: {
              editors: [],
              signers: [], // will be filled in later
            },
          },
        ],
      };
      // this type of composite only has insured tabs
      this.assignTabsToComposite(composite, signerList, envelopeTemplate.tabs, null, null);

      if (isSignature) {
        composites.splice(1, null, composite);
      } else {
        composites.push(composite);
      }
    }

    const retval = {
      request: {
        emailSubject: 'Sign Your Life Insurance Application',
        status: doSend ? 'sent' : 'created',
        allowRecursiveRecipients: 'true',
        allowReassign: 'false',
        compositeTemplates: composites,
        brandId: undefined,
        eventNotification: {
          url: this.amorphic.config.nconf.get('coverpathDocuSignCallbackUrl'),
          requireAcknowledgment: 'true',
          loggingEnabled: 'true',
          includeHMAC: 'true',
          envelopeEvents: [
            {
              envelopeEventStatusCode: 'Completed',
              includeDocuments: 'false',
            },
          ],
          eventData: {
            version: 'legacy',
            format: 'xml',
          },
          includeTimeZone: 'true',
          includeEnvelopeVoidReason: 'true',
        },
      },
      log: log,
      envelopeMap: envMap,
    };

    let brandIdConfig = 'docusignBrandId';

    if (language === 'es') {
      brandIdConfig = brandIdConfig + 'Es';
    }

    retval.request.brandId = this.amorphic.config.configStore[policy.sourceAppName].get(brandIdConfig);

    return Promise.resolve(retval);

    //make form fields into Docusign tabs
    function getFormTabs(listTabs, radioGroupTabs, checkBoxTabs, textTabs, form, param, log) {
      function resolveParam(ref) {
        return ref.replace(/Varying\(\)/, 'Varying(' + param + ')');
      }
      let value;
      log.push('---Mapping Form: ' + form.name + ' has ' + form.fields.length + ' fields');

      // Walk through all fields
      for (let formIndex = 0; formIndex < form.fields.length; ++formIndex) {
        const field = form.fields[formIndex];
        let label;

        // Make sure entity and property exist
        if (field.getEntity() && field.getProperty()) {
          // Determine whether array and how many times it repeats
          let entity = field.entity.replace(/^customer\./);
          let refTokens = entity.split('.');
          let ref = '';
          let repeat = 1;

          for (let tokenIndex = 0; tokenIndex < refTokens.length; ++tokenIndex) {
            ref += (ref.length > 0 ? '.' : '') + refTokens[tokenIndex];

            if (this.isArrayEntity[ref]) {
              refTokens[tokenIndex] = refTokens[tokenIndex] + this.getIteratorPrefix();
              repeat = this.getValue('this.customer.' + resolveParam(ref) + '.length');
              break;
            }
          }

          // Go through each iteration (non-arrays repeat once)
          for (let fieldIterator = 0; fieldIterator < repeat; ++fieldIterator) {
            // Update array reference
            label = field.labelKey || field.groupName;

            entity = field.entity.replace(/^customer\./);
            refTokens = entity.split('.');
            ref = '';

            for (let secondTokenIndex = 0; secondTokenIndex < refTokens.length; ++secondTokenIndex) {
              ref += (ref.length > 0 ? '.' : '') + refTokens[secondTokenIndex];

              if (this.isArrayEntity[ref]) {
                refTokens[secondTokenIndex] = refTokens[secondTokenIndex] + this.getIteratorPrefix();
                label = label.replace(/\*/, fieldIterator + 1);
              }
            }

            ref = 'this.customer.' + resolveParam(refTokens.join('.'));
            this.customer.fieldIterator = fieldIterator;

            // Entity must exist
            if (!this.getValue(ref)) {
              log.push(label + '=' + value + ' Entity Missing');
              continue;
            }

            // Look for entity level depends on
            if (!ref.match(/[\(\[]/)) {
              const entityDependsOn = this.getValue('typeof(' + ref + 'DependsOn)');

              if (entityDependsOn === 'function' && !this.getValue(ref + 'DependsOn()')) {
                log.push(label + '=' + value + ' Entity DependsOn');
                continue;
              }
            }

            const properties = this.getValue(ref + '.amorphicClass.getProperties()["' + field.property + '"]');

            ref = resolveParam(ref + '.' + field.property);

            // Look for property level depends on
            if (!ref.match(/\(/)) {
              const propDependsOn = this.getValue('typeof(' + ref + 'DependsOn)');

              if (propDependsOn === 'function' && !this.getValue(ref + 'DependsOn()')) {
                log.push(label + '=' + value + ' Prop DependsOn');
                continue;
              }
            }

            // Grab the value
            value = this.getValue(ref);

            if (properties && properties.format) {
              this.controller.value = value;
              value = this.controller[properties.format].call();
            } else if (value instanceof Date) {
              value = Utils.dateToMdy(value);
            }

            log.push(label + '=' + value);

            // put into a tab field for docuSign based on type. '\\*' sets all same-labelled fields at once
            if (field instanceof FormFieldText) {
              textTabs.push({
                tabLabel: '\\*' + label,
                value: value || '',
                locked: true,
              });
            }

            if (field instanceof FormFieldCheckbox) {
              if (field.booleanValue && value !== null) {
                checkBoxTabs.push({
                  tabLabel: '\\*' + label,
                  selected: value.toString() === field.booleanValue,
                  locked: true,
                });
              } else {
                checkBoxTabs.push({
                  tabLabel: '\\*' + label,
                  selected: value === 'true',
                  locked: true,
                });
              }
            }

            if (field instanceof FormFieldRadioGroup) {
              const values = {};
              let gotValues = false;

              for (let index = 0; index < field.buttons.length; index++) {
                if (field.buttons[index].key && field.buttons[index].value) {
                  if (field.buttons[index].key.match(/;/)) {
                    const listToIterate = field.buttons[index].key.split(';');

                    for (let x = 0; x < listToIterate.length; x++) {
                      values[listToIterate[x]] = field.buttons[index].value;
                    }
                  } else {
                    values[field.buttons[index].key] = field.buttons[index].value;
                  }

                  gotValues = true;
                }
              }

              if (gotValues) {
                value = values[String(value)];
              } else if (value === true) {
                value = 'Yes';
              } else if (value === false || value === null) {
                value = 'No';
              }

              if (value) {
                radioGroupTabs.push({
                  groupName: label,
                  radios: [
                    {
                      value: value.toString(),
                      selected: true,
                      locked: true,
                    },
                  ],
                });
              } else {
                log[log.length - 1] = log[log.length - 1] + '  !! Not Mapped !!';
              }
            }

            if (field instanceof FormFieldSelect) {
              listTabs.push({
                tabLabel: '\\*' + label,
                listItems: [
                  {
                    value: value,
                    selected: true,
                  },
                ],
                locked: true,
              });
            }
          }
        } else {
          log.push(label + ' Missing entity or prop on field');
        }
      }
    }
  }

  async addAllPdfsToComposites(policy: CoverpathPolicy, isAdditionalPackage?: boolean) {
    const formsAndComposites = {
      forms: this.forms,
      composites: [],
    };
    if (isAdditionalPackage) {
      return formsAndComposites;
    }
    const compositePdfObject = await this.createFormAndComposites(policy);
    formsAndComposites.composites.push(compositePdfObject);
    //Only CP uses
    if (CoverpathDocuSign.includeIllustrationAsPDF(policy)) {
      const applicationInBase64 = await this.generateIllustrationPdf(policy);

      const documentId = ILLUSTRATION_PDF_DOCUMENT_ID;
      const formName = CoverpathDocuSign.getIllustrationFormName(policy);
      const compositePdfObject = await this.generateCompositePdf(applicationInBase64, documentId, '889', formName);
      formsAndComposites.composites.push(compositePdfObject);
    }

    //Only CP
    const isPolicyMec = await CoverpathDocuSign.includeMecAsPDF(policy);
    if (isPolicyMec) {
      const mecInBase64 = await this.generateMecFormPdf(policy);
      const documentId = MEC_PDF_DOCUMENT_ID;
      const formName = CoverpathUtils.getCoverpathMecFormData(
        policy.application.primaryPolicyWithPendingAmendment
      ).templateName.replace('.pdf', '');
      const compositePdfObject = await this.generateCompositePdf(mecInBase64, documentId, '890', formName);
      formsAndComposites.composites.push(compositePdfObject);
    }

    return formsAndComposites;
  }

  async generateIllustrationPdf(_policy: CoverpathPolicy): Promise<string> {
    const primaryPolicyWithPendingAmendment = _policy.application.primaryPolicyWithPendingAmendment;
    const customer = this.customer as CoverpathCustomer;

    await customer.fetchQuotes();
    const request: IllustrationRequest = IllustrationService.createPolicyIllustrationRequest(
      primaryPolicyWithPendingAmendment.primaryPolicyAgent.primaryAgent,
      primaryPolicyWithPendingAmendment
    );

    //use amended values to generate BI for accelerated policies
    if (primaryPolicyWithPendingAmendment.isAccelerated()) {
      IllustrationService.updateIllustrationRequestWithAmendmentValues(request, primaryPolicyWithPendingAmendment);
    }

    const illustrationRequestProperties = RevisedBasicIllustrationUtils.serializeIllustrationRequest(request);
    await this.deleteExistingIllustrationRequestProperties(primaryPolicyWithPendingAmendment);
    await this.persistIllustrationRequestProperties(primaryPolicyWithPendingAmendment, illustrationRequestProperties);

    const htmlPdfController = HtmlPdfController.new();
    const rawContent = await htmlPdfController.generateBasicIllustrationPDF(request, false);
    const illustration64 = rawContent.toString('base64');

    const documentName = CoverpathDocuSign.getIllustrationFormName(primaryPolicyWithPendingAmendment);
    UILoggerSingleton.getInstance().info(
      {
        module: 'CoverpathDocuSign',
        function: 'generateIllustrationPdf',
        category: UICategory.request,
      },
      'Generated PDF of name ' +
        documentName +
        ' and byte length ' +
        illustration64.length +
        ' for policy._id:' +
        primaryPolicyWithPendingAmendment._id
    );

    return Promise.resolve(illustration64);
  }

  static getIllustrationFormName(policy: Policy): string {
    return 'BasicIllustration';
  }

  static includeIllustrationAsPDF = function (policy) {
    const primaryPolicyWithPendingAmendment = policy.application.primaryPolicyWithPendingAmendment;
    UILoggerSingleton.getInstance().info(
      {
        module: 'CoverpathDocuSign',
        function: 'includeIllustrationAsPDF',
        category: UICategory.request,
      },
      'Determining if generated basic illustration should be generated policy.product=' +
        primaryPolicyWithPendingAmendment.productWithPendingAmendment() +
        ' for policy._id=' +
        primaryPolicyWithPendingAmendment._id
    );
    return primaryPolicyWithPendingAmendment.isWholeLifePolicyWithPendingAmendment();
  };

  static includeMecAsPDF = async function (policy) {
    const primaryPolicyWithPendingAmendment = policy.application.primaryPolicyWithPendingAmendment;
    const isPolicyMec = await RevisedBasicIllustrationUtils.isPolicyMecForBI(primaryPolicyWithPendingAmendment);
    UILoggerSingleton.getInstance().info(
      {
        module: 'CoverpathDocuSign',
        function: 'includeMecAsPDF',
        category: UICategory.request,
      },
      'Determining if mec form should be generated policy.product=' +
        primaryPolicyWithPendingAmendment.product +
        ' for policy._id=' +
        primaryPolicyWithPendingAmendment._id +
        ' Answer is : ' +
        isPolicyMec
    );
    return isPolicyMec;
  };

  async generateMecFormPdf(policy: CoverpathPolicy): Promise<string> {
    const formData = CoverpathUtils.getCoverpathMecFormData(policy.application.primaryPolicyWithPendingAmendment);
    formData.mappings['TaxId1'] = await SecurityUtils.getPiiValueByKey(formData.mappings['TaxId1'], 0, true);
    formData.mappings['TaxId2'] = await SecurityUtils.getPiiValueByKey(formData.mappings['TaxId2'], 0, true);
    const formBody = await this.pdfController.fillForms([formData], false);
    return formBody[0].body;
  }

  async deleteExistingIllustrationRequestProperties(policy: Policy) {
    const existingProps = await IllustrationRequestProperty.persistorFetchByQuery({
      policy_id: policy._id,
      illustrationType: 'originalBasicIllustration',
    });

    if (existingProps.length) {
      UILoggerSingleton.getInstance().info(
        {
          module: 'CoverpathDocuSign',
          function: 'deleteExistingIllustrationRequestProperties',
          category: UICategory.request,
          data: { policyNumber: policy.policyNumber },
        },
        `Will remove ${existingProps.length} existing IllustrationRequestProperties for policy ${policy.policyNumber}`
      );

      const txn = this.amorphic.begin(true);
      existingProps.forEach((prop) => prop.persistDelete({ transaction: txn }));
      await this.amorphic.end(txn);
    }
  }

  async persistIllustrationRequestProperties(
    policy: Policy,
    illustrationRequestProperties: Array<IllustrationRequestProperty>
  ) {
    const txn = this.amorphic.begin(true);
    illustrationRequestProperties.forEach((prop) => {
      prop.policy = policy;
      prop.illustrationType = 'originalBasicIllustration';
      prop.setDirty(txn);
    });
    await this.amorphic.end(txn);
  }

  private async getSignaturePageNumber(policy: Policy) {
    const primaryPolicy = policy.application.primaryPolicyWithPendingAmendment as CoverpathPolicy;
    const waiverUnits = primaryPolicy.waiver ? primaryPolicy.faceAmount : 0;
    const alirRider: PolicyRider = primaryPolicy.getRider(RiderType.ALIR);
    const exchange1035ForAlir: Exchange1035 = alirRider && alirRider.exchange1035;
    let cashValueTransfer = 0;
    const isMec = await CoverpathDocuSign.includeMecAsPDF(policy);
    if (alirRider && exchange1035ForAlir) {
      cashValueTransfer = exchange1035ForAlir.cashValueTransfer;
    }

    return HtmlPdfUtils.getSignaturePageNumber(
      primaryPolicy.riders,
      primaryPolicy.dividendOption,
      waiverUnits,
      cashValueTransfer,
      isMec
    );
  }
}
